import { useState } from "react";
import { toast } from "sonner";
import { Loader2, MapPin, CheckCircle2 } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { authApi, formatApiErrorDetail } from "../../lib/authApi";
import { SHIP_TO_LOCKED } from "./OrderShared";

const FIELDS = [["name", "Receiver name *"], ["phone", "Phone"], ["address", "Address *"], ["city", "City *"], ["state", "State *"], ["pincode", "PIN code *"], ["instructions", "Delivery instructions"]];

export const ShipToForm = ({ order, onUpdated }) => {
  const existing = order.delivery?.ship_to;
  const [open, setOpen] = useState(!existing);
  const [form, setForm] = useState({ name: "", phone: "", address: "", city: "", state: "", pincode: "", instructions: "", ...(existing || {}) });
  const [busy, setBusy] = useState(false);
  const locked = SHIP_TO_LOCKED(order.status);

  if (locked) return null;
  if (!open) {
    return <Button size="sm" variant="outline" onClick={() => setOpen(true)} className="mt-3 rounded-full" data-testid={`portal-shipto-edit-btn-${order.id}`}><MapPin className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Edit ship-to address</Button>;
  }
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await authApi.patch(`/orders/${order.id}/ship-to`, form);
      onUpdated(data);
      setOpen(false);
      toast.success("Ship-to address saved.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-4 p-5 rounded-lg bg-muted/40 border border-border space-y-3" data-testid={`portal-shipto-form-${order.id}`}>
      <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground font-mono-tech">Ship-to address · required before dispatch</p>
      <div className="grid sm:grid-cols-2 gap-3">
        {FIELDS.map(([k, label]) => (
          <div key={k} className={`space-y-1 ${k === "address" || k === "instructions" ? "sm:col-span-2" : ""}`}>
            <Label>{label}</Label>
            <Input value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`portal-shipto-${k}-${order.id}`} />
          </div>
        ))}
      </div>
      <div className="flex gap-3">
        <Button size="sm" disabled={busy} onClick={save} className="rounded-full" data-testid={`portal-shipto-save-btn-${order.id}`}>
          {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" strokeWidth={1.5} /> : <MapPin className="h-4 w-4 mr-1.5" strokeWidth={1.5} />} Save address
        </Button>
        {existing && <Button size="sm" variant="ghost" onClick={() => setOpen(false)} className="rounded-full" data-testid={`portal-shipto-cancel-btn-${order.id}`}>Cancel</Button>}
      </div>
    </div>
  );
};

export const ConfirmDelivery = ({ order, onUpdated }) => {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  if (order.status !== "DELIVERED") return null;
  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await authApi.post(`/orders/${order.id}/confirm-delivery`, { note });
      onUpdated(data);
      toast.success("Thank you — delivery confirmed.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-4 p-5 rounded-lg bg-emerald-500/5 border border-emerald-500/20 space-y-3" data-testid={`portal-confirm-delivery-${order.id}`}>
      <p className="text-sm">Your order has been marked delivered. Please confirm you received it.</p>
      <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional note (condition, shortages...)" className="max-w-md" data-testid={`portal-confirm-delivery-note-${order.id}`} />
      <Button size="sm" disabled={busy} onClick={confirm} className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`portal-confirm-delivery-btn-${order.id}`}>
        {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" strokeWidth={1.5} /> : <CheckCircle2 className="h-4 w-4 mr-1.5" strokeWidth={1.5} />} Confirm delivery
      </Button>
    </div>
  );
};
